import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightIcon,
  ArrowRightOnRectangleIcon,
  BoltIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  ClipboardDocumentListIcon,
  PauseCircleIcon
} from '@heroicons/react/24/outline';
import AttendanceService from '../services/attendance.service';
import { useHeaderSearch } from '../contexts/SearchContext';
import toast from '../utils/toast';
import { socket } from '../utils/socket';
import { formatTime12hFromDb } from '../utils/timeDisplay';
import { formatDateDisplay } from '../utils/dateDisplay';

const formatDurationForUi = (timeValue) => {
  if (!timeValue || typeof timeValue !== 'string') return '0m';

  const parts = timeValue.split(':').map(Number);
  const hours = Number.isNaN(parts[0]) ? 0 : parts[0];
  const minutes = Number.isNaN(parts[1]) ? 0 : parts[1];
  const totalMinutes = (hours * 60) + minutes;

  if (totalMinutes <= 0) return '0m';
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const labelHours = Math.floor(totalMinutes / 60);
  const labelMinutes = totalMinutes % 60;
  return labelMinutes === 0 ? `${labelHours}h` : `${labelHours}h ${labelMinutes}m`;
};

const isMeaningfulTime = (value) => {
  if (!value) return false;
  const text = String(value).trim();
  if (!text) return false;
  return text !== '00:00' && text !== '00:00:00';
};

const parseHmsToSeconds = (value) => {
  if (!isMeaningfulTime(value)) return 0;
  const [h = 0, m = 0, s = 0] = String(value)
    .trim()
    .split(':')
    .map((part) => Number(part));
  return (Number.isFinite(h) ? h : 0) * 3600 + (Number.isFinite(m) ? m : 0) * 60 + (Number.isFinite(s) ? s : 0);
};

const secondsToHms = (totalSeconds) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = String(Math.floor(safeSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, '0');
  const seconds = String(safeSeconds % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

const getDateWithTime = (dateValue, timeValue) => {
  if (!dateValue || !isMeaningfulTime(timeValue)) return null;
  const base = new Date(dateValue);
  if (Number.isNaN(base.getTime())) return null;

  const parts = String(timeValue).trim().split(':').map((p) => Number(p));
  const hours = Number.isFinite(parts[0]) ? parts[0] : 0;
  const minutes = Number.isFinite(parts[1]) ? parts[1] : 0;
  const seconds = Number.isFinite(parts[2]) ? parts[2] : 0;

  base.setHours(hours, minutes, seconds, 0);
  return base;
};

const parseRemarkMetaFromText = (remark) => {
  const text = typeof remark === 'string' ? remark : '';
  const marker = '|meta:';
  const idx = text.indexOf(marker);
  if (idx === -1) return { openBreakStartMs: null };

  const metaText = text.slice(idx + marker.length).trim();
  const pairs = metaText.split(',').map((item) => item.trim()).filter(Boolean);
  const meta = {};
  for (const pair of pairs) {
    const [k, v] = pair.split('=');
    if (!k) continue;
    meta[String(k).trim()] = (v ?? '').trim();
  }

  const obsMs = Number(meta.obs);
  return {
    openBreakStartMs: Number.isFinite(obsMs) && obsMs > 0 ? Math.floor(obsMs) : null,
  };
};

const getNetWorkTime = (record) => {
  if (!record?.intime || !record?.outtime) return record?.worktime || '00:00:00';

  const calculated = AttendanceService.calculateWorkHours(
    record.intime,
    record.outtime,
    record.breaktime || '00:00:00'
  );

  return calculated || record?.worktime || '00:00:00';
};

const REQUIRED_WORK_SECONDS_FOR_PRESENT = 9 * 60 * 60;

const getFrontendCalculatedStatus = (record) => {
  const rawStatus = typeof record?.status === 'string' ? record.status.trim() : '';
  if (rawStatus === 'WO') return 'Holiday';
  // Apply the same completed-punch rule to managed and unmanaged work days.
  // Keep non-work statuses such as Holiday/Leave/Absent/WFH as-is.
  if (rawStatus && rawStatus !== 'Present' && rawStatus !== 'HalfDay') return rawStatus;

  // Avoid marking HalfDay while the day is still running (no out time yet).
  if (!isMeaningfulTime(record?.intime) || !isMeaningfulTime(record?.outtime)) return rawStatus;

  const netSeconds = parseHmsToSeconds(getNetWorkTime(record));
  if (netSeconds <= 0) return rawStatus;

  return netSeconds >= REQUIRED_WORK_SECONDS_FOR_PRESENT ? 'Present' : 'HalfDay';
};

const displayClockFromDb = (value) => {
  if (!isMeaningfulTime(value)) return '--:--';
  return formatTime12hFromDb(value);
};

const getPresenceStatus = (attendance) => {
  if (typeof attendance?.presenceStatus === 'boolean') return attendance.presenceStatus;
  return Boolean(
    attendance?.intime &&
      String(attendance.intime).trim() !== '00:00:00' &&
      String(attendance.intime).trim() !== '00:00' &&
      (!attendance?.outtime ||
        String(attendance.outtime).trim() === '00:00:00' ||
        String(attendance.outtime).trim() === '00:00')
  );
};

const Attendance = () => {
  const { headerSearch } = useHeaderSearch();
  const navigate = useNavigate();
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [liveWorkTime, setLiveWorkTime] = useState('00:00:00');
  const [isWorkTimerRunning, setIsWorkTimerRunning] = useState(false);

  useEffect(() => {
    fetchTodayAttendance();
  }, []);

  useEffect(() => {
    let timerId;

    const getOpenBreakStartMs = (attendance) => {
      const direct = Number(attendance?.openBreakStartMs);
      if (Number.isFinite(direct) && direct > 0) return direct;
      return parseRemarkMetaFromText(attendance?.remark).openBreakStartMs;
    };

    const isOnBreak = (attendance) => {
      const ms = getOpenBreakStartMs(attendance);
      return Number.isFinite(ms) && ms > 0;
    };

    const computeLiveWorkTime = () => {
      if (!todayAttendance) {
        setLiveWorkTime('00:00:00');
        setIsWorkTimerRunning(false);
        return;
      }

      const recordDate = todayAttendance.datestring || new Date();
      const start = getDateWithTime(recordDate, todayAttendance.intime);
      if (!start) {
        setLiveWorkTime(todayAttendance.worktime || '00:00:00');
        setIsWorkTimerRunning(false);
        return;
      }

      const hasOutTime = isMeaningfulTime(todayAttendance.outtime);
      const isPresent = getPresenceStatus(todayAttendance);
      if (!isPresent) {
        setIsWorkTimerRunning(false);
        return;
      }
      const end = new Date();

      if (!end) {
        setLiveWorkTime(todayAttendance.worktime || '00:00:00');
        setIsWorkTimerRunning(false);
        return;
      }

      const rawSeconds = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
      setLiveWorkTime(secondsToHms(rawSeconds));
      setIsWorkTimerRunning(isPresent);
    };

    computeLiveWorkTime();

    if (
      todayAttendance &&
      isMeaningfulTime(todayAttendance.intime) &&
      getPresenceStatus(todayAttendance)
    ) {
      timerId = window.setInterval(computeLiveWorkTime, 1000);
    }

    return () => {
      if (timerId) window.clearInterval(timerId);
    };
  }, [todayAttendance]);

  useEffect(() => {
    // Handle presence status updates from iClock devices
    const handlePresenceUpdate = (update) => {
      if (!update || typeof update.presenceStatus !== 'boolean') return;
      
      console.log('🔄 Received presence status update:', update.presenceStatus);

      setTodayAttendance((prev) => {
        if (!prev) return prev;
        
        console.log('✅ Updating presence status to:', update.presenceStatus);
        
        // Only update presence status, keep all other data intact
        return {
          ...prev,
          presenceStatus: update.presenceStatus,
        };
      });
    };

    // Handle full attendance updates (for other sources)
    const handleAttendanceUpdate = (update) => {
      if (!update) return;

      const isSameDay = (a, b) => {
        if (!a || !b) return false;
        const da = new Date(a);
        const db = new Date(b);
        if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
        return (
          da.getFullYear() === db.getFullYear() &&
          da.getMonth() === db.getMonth() &&
          da.getDate() === db.getDate()
        );
      };

      setTodayAttendance((prev) => {
        const prevDate = prev?.datestring;
        const nextDate = update?.datestring;

        const today = new Date();
        const isTodayUpdate = isSameDay(nextDate, today) || (prevDate && isSameDay(prevDate, nextDate));
        if (!isTodayUpdate) return prev;

        const nextPresenceStatus =
          typeof update?.presenceStatus === 'boolean'
            ? update.presenceStatus
            : isMeaningfulTime(update?.intime) && !isMeaningfulTime(update?.outtime);

        return {
          ...(prev || {}),
          ...update,
          presenceStatus: nextPresenceStatus,
        };
      });
    };

    socket.on('attendance:presenceUpdate', handlePresenceUpdate);
    socket.on('attendance:update', handleAttendanceUpdate);
    
    return () => {
      socket.off('attendance:presenceUpdate', handlePresenceUpdate);
      socket.off('attendance:update', handleAttendanceUpdate);
    };
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      setLoading(true);
      const todayData = await AttendanceService.getTodayAttendance();
      setTodayAttendance(todayData || null);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      const errorMessage = error.message || error.response?.data?.message || error.response?.data?.error || 'Failed to load attendance data';
      toast.error(errorMessage);
      setTodayAttendance(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // Manual mark/check-out flow intentionally disabled as per UI requirement.
  // const handleMarkAttendance = async () => {};
  // const handleCheckOut = async () => {};

  const handleRequestCorrection = () => {
    navigate('/attendance-correction', { state: { newRequest: true } });
  };

  const handleViewHistory = () => {
    navigate('/attendance-history');
  };

  const handleDownloadReport = async () => {
    try {
      setActionLoading(true);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(endDate.getMonth() - 1); // Last month
      
      const blob = await AttendanceService.exportAttendanceReport(
        formatDate(startDate),
        formatDate(endDate),
        'excel'
      );
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance-report-${formatDate(startDate)}-to-${formatDate(endDate)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading report:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to download report';
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-xl mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-64 bg-gray-200 rounded-xl"></div>
            <div className="h-64 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  const presenceStatus = getPresenceStatus(todayAttendance);

  const hasLiveSession = todayAttendance && isMeaningfulTime(todayAttendance.intime);
  const workHoursHms = hasLiveSession
    ? liveWorkTime
    : todayAttendance
      ? getNetWorkTime(todayAttendance)
      : '00:00:00';
  const currentDate = new Date();
  const currentDayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <div className="min-h-full space-y-6 bg-[#F8FAFC]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#166534] to-[#16A34A] p-6 text-white shadow-sm sm:p-8"
      >
        <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Attendance Tracking</h1>
            <p className="mt-2 text-sm text-emerald-100 sm:text-base">
              Track your daily attendance and work hours.
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-sm">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15">
              <CalendarDaysIcon className="h-6 w-6 text-white" />
            </div>
            <div className="text-left sm:text-right">
              <p className="font-semibold text-white">{formatDateDisplay(currentDate)}</p>
              <p className="text-sm text-emerald-100">{currentDayName}</p>
            </div>
          </div>
        </div>
      </motion.div>

      <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
        <Card.Header className="border-slate-100 bg-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
              <ClockIcon className="h-5 w-5 text-emerald-700" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Today's Attendance</h2>
          </div>
        </Card.Header>
        <Card.Body className="px-6 pb-6 pt-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex min-h-[100px] flex-col justify-between rounded-xl border border-green-200 bg-green-50 p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                  <ClockIcon className="h-5 w-5 text-green-700" />
                </div>
                <p className="text-sm font-semibold text-green-800">Work Hours Today</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold tabular-nums text-slate-900">{workHoursHms}</p>
                {todayAttendance && isMeaningfulTime(todayAttendance.intime) && (
                  <p className="mt-1 text-xs font-medium text-green-700">
                    {presenceStatus ? 'Running' : 'Paused'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex min-h-[100px] flex-col justify-between rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                  <PauseCircleIcon className="h-5 w-5 text-amber-600" />
                </div>
                <p className="text-sm font-semibold text-amber-700">Break Time</p>
              </div>
              <p className="text-center text-xl font-bold text-slate-900">
                {formatDurationForUi(todayAttendance?.breaktime || '00:00:00')}
              </p>
            </div>

            <div className="flex min-h-[100px] flex-col justify-between rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <ArrowRightOnRectangleIcon className="h-5 w-5 text-blue-600" />
                </div>
                <p className="text-sm font-semibold text-blue-700">In Time</p>
              </div>
              <p className="text-center text-xl font-bold text-slate-900">
                {displayClockFromDb(todayAttendance?.intime)}
              </p>
            </div>

            <div className="flex min-h-[100px] flex-col justify-between rounded-xl border border-violet-200 bg-violet-50 p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-100">
                  <ArrowLeftOnRectangleIcon className="h-5 w-5 text-violet-700" />
                </div>
                <p className="text-sm font-semibold text-violet-700">Out Time</p>
              </div>
              <p className="text-center text-xl font-bold text-slate-900">
                {displayClockFromDb(todayAttendance?.outtime)}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>


      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(260px,3fr)]">
        <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
          <Card.Header className="border-slate-100 bg-white px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                  <ChartBarIcon className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Recent Attendance</h2>
                  <p className="mt-0.5 text-sm text-slate-500">Your attendance for the last 7 days.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleViewHistory}
                className="inline-flex flex-shrink-0 items-center gap-1 text-sm font-semibold text-emerald-700 transition-colors hover:text-emerald-800"
              >
                View All
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <RecentAttendanceTable key={`${todayAttendance?.intime}-${todayAttendance?.outtime}`} headerSearch={headerSearch} />
          </Card.Body>
        </Card>

        <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
          <Card.Header className="border-slate-100 bg-white px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                <BoltIcon className="h-5 w-5 text-emerald-700" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Quick Actions</h2>
            </div>
          </Card.Header>
          <Card.Body className="p-6">
            <div className="space-y-3">
              <Button
                variant="attendancePrimary"
                className="h-12 w-full justify-start px-4 font-semibold"
                icon={ClipboardDocumentListIcon}
                onClick={handleRequestCorrection}
              >
                Request Correction
              </Button>
              <Button
                variant="attendanceSecondary"
                className="h-12 w-full justify-start px-4 font-semibold"
                icon={ClockIcon}
                onClick={handleViewHistory}
              >
                View History
              </Button>
              <Button
                variant="attendanceNeutral"
                className="h-12 w-full justify-start px-4 font-semibold"
                icon={DocumentArrowDownIcon}
                onClick={handleDownloadReport}
                loading={actionLoading}
              >
                Download Report
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

const RecentAttendanceTable = ({ headerSearch = '' }) => {
  const [recentData, setRecentData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentAttendance();
  }, []);

  const fetchRecentAttendance = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 7);

      const response = await AttendanceService.getAttendance(
        formatDate(startDate),
        formatDate(endDate)
      );

      setRecentData(response?.attendanceData || []);
    } catch (error) {
      console.error('Error fetching recent attendance:', error);
      const errorMessage = error.message || error.response?.data?.message || error.response?.data?.error || 'Failed to load recent attendance';
      toast.error(errorMessage);
      setRecentData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-200 rounded"></div>
        ))}
      </div>
    );
  }

  const filteredData = headerSearch
    ? recentData.filter(record => {
        const q = headerSearch.toLowerCase();
        const displayStatus = (getFrontendCalculatedStatus(record) || record.status || '').toLowerCase();
        return (
          (displayStatus && displayStatus.includes(q)) ||
          (record.datestring && formatDateDisplay(record.datestring).includes(q)) ||
          (record.intime && record.intime.includes(q)) ||
          (record.remark && record.remark.toLowerCase().includes(q))
        );
      })
    : recentData;

  const today = new Date();
  const isSameDay = (a, b) => {
    if (!a || !b) return false;
    const da = new Date(a);
    const db = new Date(b);
    if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  };

  const displayData = filteredData.filter((record) => !isSameDay(record?.datestring, today));

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
              In Time
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Out Time
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Work Hours
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Break Time
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {displayData.slice(0, 7).map((record, index) => {
            const status = getFrontendCalculatedStatus(record) || record.status || '--';
            const normalizedStatus = String(status).replace(/\s+/g, '').toLowerCase();
            const statusClasses = normalizedStatus === 'present'
              ? 'border-green-200 bg-green-50 text-green-800'
              : normalizedStatus === 'holiday' || normalizedStatus === 'absent'
                ? 'border-red-200 bg-red-50 text-red-700'
                : normalizedStatus === 'halfday'
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : normalizedStatus.includes('leave')
                    ? 'border-violet-200 bg-violet-50 text-violet-800'
                    : 'border-slate-200 bg-slate-50 text-slate-700';
            return (
            <tr key={index} className="transition-colors hover:bg-slate-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                {formatDateDisplay(record.datestring)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses}`}>
                  {status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                {displayClockFromDb(record.intime)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                {displayClockFromDb(record.outtime)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                {formatDurationForUi(getNetWorkTime(record))}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                {formatDurationForUi(record.breaktime || '00:00:00')}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Attendance;

